import { http } from '../../http';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';
import Apps from '@/core/Apps';

export default {
    state: {
        vouchersLoading: false,
        productsLoading: false,
        productsLoaded: false,
        productGroups: [],
        products: [],
        addons: [],
        vouchers: [],
    },

    mutations: {
        /**
         * Products
         */
        setProducts(state, products) {
            state.products = products || [];
        },

        addOrUpdateProduct(state, product) {
            if (!product.hasOwnProperty('addon_ids')) {
                product.addon_ids = (product.addons || []).map(addon => addon.id);
            }

            let index = state.products.findIndex(p => p.id === product.id);

            if (index >= 0) {
                state.products[index] = product;
            } else {
                state.products.push(product);
            }

            (product.addon_ids || []).forEach(addonId => {
                let addon = state.addons.find(a => a.id === addonId);

                if (addon) {
                    addon.product_ids = uniq([...addon.product_ids, product.id]);
                }
            });
        },

        deleteProduct(state, product) {
            state.products = state.products.filter(p => p.id !== product.id);
            state.addons = state.addons.filter(a => a.id !== product.id);
            state.vouchers = state.vouchers.filter(v => v.product_id !== product.id);
        },

        /**
         * Addons
         */
        setAddons(state, addons) {
            state.addons = addons || [];
        },

        addOrUpdateAddon(state, addon) {
            let index = state.addons.findIndex(a => a.id === addon.id);

            if (addon.id && index >= 0) {
                state.addons[index] = addon;
            } else {
                state.addons.push(addon);
            }

            (addon.product_ids || []).forEach(addonProductId => {
                let product = state.products.find(p => p.id === addonProductId);

                if (product) {
                    product.addon_ids = uniq([...product.addon_ids, addon.id]);
                }
            })

            // products, that should NOT have this addon any more
            state.products.filter(p => !addon.product_ids.includes(p.id))
                .forEach(product => {
                    if (product.hasOwnProperty('addon_ids')) {
                        product.addon_ids = product.addon_ids.filter(addon_id => addon_id !== addon.id);
                    }

                    if (product.hasOwnProperty('addons')) {
                        product.addons = product.addons.filter(a => a.id !== addon.id);
                    }
                });
        },

        deleteAddon (state, addonToDelete) {
            let addonToDeleteId = typeof addonToDelete == 'object' ? addonToDelete.id : addonToDelete;
            state.addons = state.addons.filter(addon => addon.id !== addonToDeleteId);
        },

        /**
         * Product Groups
         */
        setProductGroups(state, groups) {
            state.productGroups = groups;
        },

        addOrUpdateProductGroup(state, group) {
            let index = state.productGroups.findIndex(pg => pg.id === group.id);

            if (group.id && index >= 0) {
                state.productGroups[index] = group;
            } else {
                state.productGroups.push(group);
            }
        },

        deleteGroup(state, group) {
            state.productGroups = state.productGroups.filter(pg => pg.id !== group.id);
            state.products.filter(p => p.product_group_id === group.id).forEach(p => { p.product_group_id = 0 });
            group.vouchers.forEach(v => { v.product_group_id = 0; });
        },

        /**
         * Vouchers
         */
        setVouchers(state, vouchers) {
            state.vouchers = vouchers || [];
        },

        addOrUpdateVoucher(state, voucher) {
            let index = state.vouchers.findIndex(v => v.id === voucher.id);

            if (voucher.id && index >= 0) {
                state.vouchers[index] = voucher;
            } else {
                state.vouchers.push(voucher);
            }
        },

        deleteVoucher(state, voucher) {
            state.vouchers = state.vouchers.filter(v => v.id !== voucher.id);
        },

        deleteProductVouchers(state, product) {
            state.vouchers = state.vouchers.filter(v => v.product_id !== product.id);
        },

        /**
         * Other
         */
        setProductsLoaded(state, loaded = true) {
            state.productsLoaded = loaded;
        },

        setProductsLoading(state, isLoading = true) {
            state.productsLoading = isLoading;
        },

        setVouchersLoading(state, isLoading = true) {
            state.vouchersLoading = isLoading;
        },
    },

    getters: {
        products: (state, getters, rootState, rootGetters) => {
            return [...(state.products || []).map(product => {
                product.addons = (product.addon_ids || []).map(addon_id => {
                    return state.addons.find(a => a.id === addon_id);
                }).filter(addon => Boolean(addon));

                if (product.calendar_ids && product.calendar_ids.length > 0) {
                    product.calendars = (product.calendar_ids || []).map(calendar_id => {
                        return rootGetters['calendars/getCalendarById'](calendar_id);
                    }).filter(calendar => Boolean(calendar));
                } else {
                    product.calendars = product.calendars || [];
                }

                return product;
            })].sort(sortByName);
        },

        addons: (state, getters) => {
            return [...(state.addons || []).map(addon => {
                addon.products = (addon.product_ids || []).map(product_id => {
                    return state.products.find(p => p.id === product_id);
                }).filter(product => Boolean(product));

                return addon;
            })].sort(sortByName);
        },

        productGroups: (state, getters) => {
            return [...(state.productGroups || []).map(pg => {
                pg.products = [...getters.products.filter(p => p.product_group_id === pg.id)].sort(sortByOrder);
                pg.vouchers = getters.getOrderedVouchers('').filter(v => v.product_group_id === pg.id);

                return pg;
            })].sort(sortByOrder);
        },

        ungroupedProducts: (state, getters) => {
            return getters.products.filter(p => !p.product_group_id);
        },

        getProductById: (state, getters) => (product_id) => {
            product_id = parseInt(product_id);
            return getters.products.find(p => p.id === product_id);
        },

        getGroupById: (state, getters) => (group_id) => {
            return getters.productGroups.find(pg => pg.id === group_id);
        },

        getOrderedGroupProducts: (state, getters) => (group, search_word) => {
            // We work on a copy of the product list, since we want to make sure we don't override the original.
            let products = group.products.slice(0);

            if (search_word) {
                search_word = search_word.toLowerCase();
                products = products.filter(product => {
                    return product.name.toLowerCase().includes(search_word);
                });
            }

            return products.sort(sortByOrder);
        },

        getOrderedGroupVouchers: (state, getters) => (group, search_word) => {
            // We work on a copy of the voucher list, since we want to make sure we don't override the original.
            let vouchers = group.vouchers.slice(0);

            if (search_word) {
                search_word = search_word.toLowerCase();
                vouchers = vouchers.filter(voucher => {
                    return voucher.name.toLowerCase().includes(search_word);
                });
            }

            return vouchers.sort(sortByOrder);
        },

        getOrderedGroupEntries: (state, getters) => (group, includeVouchers, search_word) => {
            let entries = getters.getOrderedGroupProducts(group, search_word).map(
                p => ({ isProduct: true, value: p })
            );

            if (includeVouchers) {
                let vouchers =getters.getOrderedGroupVouchers(group, search_word).map(
                    v => ({ isProduct: false, value: v })
                );

                entries = entries.concat(vouchers);
            }

            return entries.sort((a, b) => sortByOrder(a.value, b.value));
        },

        getOrderedUngroupedEntries: (state, getters) => (includeVouchers, search_word) => {
            let entries = getters.getOrderedUngroupedProducts(search_word).map(
                p => ({ isProduct: true, value: p })
            );

            if (includeVouchers) {
                let vouchers = getters.getOrderedUngroupedVouchers(search_word).map(
                    v => ({ isProduct: false, value: v })
                );

                entries = entries.concat(vouchers);
            }

            return entries.sort((a, b) => sortByOrder(a.value, b.value));
        },

        getOrderedProductGroups: (state, getters) => (search_word) => {
            let groups = [...getters.productGroups];

            if (search_word) {
                search_word = search_word.toLowerCase();

                groups = groups.filter(group => {
                    return group.name.toLowerCase().includes(search_word)
                        || group.products.some(p => p.name.toLowerCase().includes(search_word));
                })
            }

            return groups.sort(sortByOrder);
        },

        getOrderedUngroupedProducts: (state, getters) => (search_word) => {
            let products = [...getters.ungroupedProducts];

            if (search_word) {
                search_word = search_word.toLowerCase();

                products = products.filter(product => {
                    return product.name.toLowerCase().includes(search_word);
                });
            }

            return products.sort(sortByOrder);
        },

        getOrderedUngroupedVouchers: (state, getters) => (search_word) => {
            return getters.getOrderedVouchers(search_word).filter(voucher => parseInt(voucher.product_group_id) === 0);
        },

        getProductGroupsForVSelect: (state, getters) => ({search = null, excludeIds = []} = {}) => {
            let ungroupedProducts = getters.getOrderedUngroupedProducts(search);

            let groups = cloneDeep(getters.getOrderedProductGroups(search))
                // add the ungrouped services
                .concat([{
                    id: 0,
                    name: trans('online_booking.products_no_group'),
                    order: 99999,
                    products: ungroupedProducts,
                }])

                // filter out the excluded product IDs
                .map(group => {
                    group.products = group.products.filter(product => !excludeIds.includes(product.id));
                    return group;
                })

                // exclude empty groups
                .filter(group => group.products.length > 0)

                // convert the items for VSelect to understand
                .map(group => {
                    return {
                        label: group.name,
                        options: group.products.map(({ id, name }) => ({ id, label: name }))
                    };
                });

            return groups;
        },

        getVoucherById: (state, getters) => (voucher_id) => {
            return find(state.vouchers, ['id', parseInt(voucher_id)]);
        },

        getVouchersForEvents: (state, getters) => () => {
            return getters.getOrderedVouchers('').filter(voucher => voucher.is_for_events);
        },

        getVouchersForProduct: (state, getters) => (product) => {
            let productId = typeof product === 'object' ? product.id : parseInt(product);

            return getters.getOrderedVouchers().filter(voucher => voucher.product_id === productId);
        },

        getOrderedVouchers: (state, getters) => (search_word) => {
            let vouchers = state.vouchers.slice(0);

            if (search_word) {
                search_word = search_word.toLowerCase();

                vouchers = vouchers.filter(voucher => {
                    return voucher.name.toLowerCase().includes(search_word);
                });
            }

            return vouchers.sort(sortByOrder);
        },

        getOrderedAddons: (state, getters) => (search_word) => {
            let addons = [...getters.addons];

            if (search_word) {
                search_word = search_word.toLowerCase();

                addons = addons.filter(addon => addon.name.toLowerCase().includes(search_word));
            }

            return addons.sort(sortByName);
        },

        groupedProductCount(state, getters) {
            return getters.productGroups.reduce((acc, group) => acc + group.products.length, 0);
        },

        ungroupedProductCount(state, getters) {
            return getters.ungroupedProducts.length;
        },

        productCount(state, getters) {
            return getters.groupedProductCount + getters.ungroupedProductCount;
        },

        productGroupCount(state, getters) {
            return getters.productGroups.length;
        },

        vouchersCount(state, getters) {
            return objectToArray(state.vouchers).length;
        },

        getProductsLoaded(state, getters) {
            return state.productsLoaded;
        },
    },

    actions: {

        forceLoadData ({ dispatch }) {
            dispatch('loadProducts');
            dispatch('loadVouchers');
        },

        loadData ({ dispatch, state }) {
            dispatch('calendars/loadCalendars', null, { root: true });

            if (!state.productsLoading && !state.productsLoaded) {
                dispatch('loadProducts');
            }

            if (!state.vouchersLoading) {
                dispatch('loadVouchers');
            }
        },

        loadProducts ({ state, commit }) {

            if (state.productsLoading) {
                return;
            }

            commit('setProductsLoading', true);

            http.get('/app/product-groups')
                .then(({ data }) => {
                    commit('setProducts', data.products);
                    commit('setAddons', data.addons);
                    commit('setProductGroups', data.product_groups);
                    commit('setProductsLoading', false);
                    commit('setProductsLoaded');
                }).catch(() => {
                    commit('setProductsLoading', false);
                });
        },

        loadVouchers ({ state, commit, rootGetters }) {
            if (!rootGetters['apps/isAppEnabled'](Apps.Vouchers)) {
                return;
            }

            if (state.vouchersLoading) {
                return;
            }

            commit('setVouchersLoading', true);

            http.get('/app/vouchers')
                .then(({ data }) => {
                    commit('setVouchers', data);
                    commit('setVouchersLoading', false);
                }).catch(() => {
                    commit('setVouchersLoading', false);
                });
        }
    }
};

function objectToArray(obj) {
    return Object.keys(obj).map((key) => { return obj[key]; });
}

function sortByName(itemA, itemB) {
    if (itemA.name.toLowerCase() > itemB.name.toLowerCase()) {
        return 1;
    }
    if (itemA.name.toLowerCase() < itemB.name.toLowerCase()) {
        return -1;
    }
    if (itemA.name.toLowerCase() == itemB.name.toLowerCase()) {
        return 0;
    }
}

function sortByOrder(itemA, itemB) {
    if (itemA.order > itemB.order) {
        return 1;
    }
    if (itemA.order < itemB.order) {
        return -1;
    }
    if (itemA.order == itemB.order) {
        return sortByName(itemA, itemB);
    }
}
